import { WordItem } from '../types';
import { CrosswordGrid, CrosswordWord, CrosswordWordCell } from '../data/crosswordData';

interface NormalizedWord {
  id: string;
  cleanKab: string; // Uppercase letters (or words separated by space)
  originalKab: string;
  fr: string;
  category: string;
  icon?: string;
  culturalNote?: string;
  phonetic?: string;
  isPhrase?: boolean;
  phraseWords?: string[]; // Individual clean words of the phrase
}

interface GridCellPlacement {
  char: string;
  acrossWordId: string | null;
  downWordId: string | null;
  isSpace?: boolean;
}

/**
 * Normalizes a Kabyle word string for crossword grid placement.
 * Retains standard Latin letters and special Berber characters (Ɛ, Ɣ, Ḥ, Ṣ, Ṭ, Ḍ, Č, Ǧ, Ž, Ṛ, Ẓ).
 */
export function normalizeCrosswordString(raw: string): string {
  if (!raw) return '';
  return raw
    .trim()
    .toUpperCase()
    .replace(/[^A-Z\u00C0-\u024F\u1E00-\u1EFF\u025B\u0263\u1E25\u1E63\u1E6D\u1E0D\u010C\u01E6\u017D\u1E5B\u1E93]/g, '');
}

/**
 * Checks if a Kabyle raw string is a phrase or multi-word expression.
 */
export function isPhraseString(raw: string): boolean {
  if (!raw) return false;
  const trimmed = raw.trim();
  if (trimmed.includes(' ')) return true;
  // Hyphen connecting letters (e.g. Tekkiɣ-d, fell-awen, isem-ik)
  if (/[-][a-zA-Z\u00C0-\u024F\u1E00-\u1EFF\u025B\u0263\u1E25\u1E63\u1E6D\u1E0D\u010C\u01E6\u017D\u1E5B\u1E93]/i.test(trimmed)) {
    return true;
  }
  return false;
}

/**
 * Splits a phrase or expression into its individual clean words.
 */
export function extractPhraseWords(raw: string): string[] {
  if (!raw) return [];
  const cleaned = raw.replace(/[?,.!;:"'()]/g, ' ').trim();
  const parts = cleaned.split(/[\s\-]+/).filter(Boolean);
  const words: string[] = [];
  for (const part of parts) {
    const norm = normalizeCrosswordString(part);
    if (norm.length > 0) {
      words.push(norm);
    }
  }
  return words;
}

/**
 * Lays out words of a phrase horizontally across rows, wrapping to the next row
 * ("retour à la ligne si nécessaire") and inserting dedicated space cells between words.
 */
export function layoutPhraseHorizontal(
  phraseWords: string[],
  startRow: number
): { cells: CrosswordWordCell[]; height: number; width: number } {
  const cells: CrosswordWordCell[] = [];
  let c = 0;
  const r = startRow;

  for (let wIdx = 0; wIdx < phraseWords.length; wIdx++) {
    const word = phraseWords[wIdx];
    // Insert space cell between words on the same row
    if (wIdx > 0) {
      cells.push({
        row: r,
        col: c,
        char: ' ',
        isSpace: true,
      });
      c++;
    }
    // Insert letters of the word
    for (let i = 0; i < word.length; i++) {
      cells.push({
        row: r,
        col: c,
        char: word[i],
        isSpace: false,
      });
      c++;
    }
  }

  return {
    cells,
    height: 1,
    width: c,
  };
}

/** Maximum allowed words per crossword grid to maintain responsive, comfortable gameplay */
export const MAX_WORDS_PER_GRID = 25;

/**
 * Generates an evolving crossword grid containing words for a challenge.
 * - Caps horizontal width so it NEVER overflows mobile screens.
 * - Prioritizes horizontal layouts for phrases with line-breaks and space cells.
 * - Words attach sequentially for progressive reveal.
 */
export function generateCrosswordForChallenge(
  challengeId: string,
  title: string,
  category: string,
  words: WordItem[],
  options?: {
    icon?: string;
    region?: string;
    requiredLevel?: number;
    maxCols?: number;
  }
): CrosswordGrid {
  const MAX_COLS = options?.maxCols ?? 8;
  const wordsToUse = words.slice(0, MAX_WORDS_PER_GRID);

  // 1. Filter, deduplicate, and normalize words (detecting phrases)
  const normalizedWords: NormalizedWord[] = [];
  const seenKab = new Set<string>();
  const seenFr = new Set<string>();

  wordsToUse.forEach((w, idx) => {
    const isPhrase = isPhraseString(w.kab);
    let phraseWords: string[] | undefined = undefined;
    let clean: string;

    if (isPhrase) {
      phraseWords = extractPhraseWords(w.kab);
      if (phraseWords.length > 1) {
        clean = phraseWords.join(' ');
      } else {
        clean = normalizeCrosswordString(w.kab);
      }
    } else {
      clean = normalizeCrosswordString(w.kab);
    }

    if (clean.replace(/\s+/g, '').length < 2) return; // Discard single-letter tokens

    const dedupeKeyKab = clean.toLowerCase();
    const dedupeKeyFr = (w.fr || '').toLowerCase().trim();
    if (seenKab.has(dedupeKeyKab) || seenFr.has(dedupeKeyFr)) return;
    seenKab.add(dedupeKeyKab);
    seenFr.add(dedupeKeyFr);

    normalizedWords.push({
      id: `word_${challengeId}_${idx + 1}`,
      cleanKab: clean,
      originalKab: w.kab,
      fr: w.fr,
      category: w.type || category,
      icon: w.icon || options?.icon,
      culturalNote: `Vocabulaire kabyle authentique du thème « ${category} ».`,
      phonetic: w.kab,
      isPhrase: isPhrase && (phraseWords?.length || 0) > 1,
      phraseWords,
    });
  });

  // Handle empty fallback
  if (normalizedWords.length === 0) {
    return {
      id: `grid_challenge_${challengeId}`,
      title,
      subtitle: `Défi • ${category}`,
      difficulty: 'Initié',
      icon: options?.icon || '🧩',
      rows: 5,
      cols: MAX_COLS,
      requiredLevel: options?.requiredLevel ?? 0,
      region: options?.region || 'Kabylie',
      description: `Mots-croisés thématiques : ${title}.`,
      words: [],
      xpReward: 50,
      isEvolving: true,
    };
  }

  // 2. Order words sequentially
  const orderedWords: NormalizedWord[] = [];
  const remaining = [...normalizedWords];

  // Pick first word
  remaining.sort((a, b) => {
    const aFits = a.isPhrase || a.cleanKab.length <= MAX_COLS ? 1 : 0;
    const bFits = b.isPhrase || b.cleanKab.length <= MAX_COLS ? 1 : 0;
    if (aFits !== bFits) return bFits - aFits;
    return b.cleanKab.length - a.cleanKab.length;
  });
  orderedWords.push(remaining.shift()!);

  while (remaining.length > 0) {
    const placedChars = new Set(
      orderedWords.flatMap((w) => w.cleanKab.replace(/\s+/g, '').split(''))
    );
    let bestIdx = -1;
    let bestScore = -1;

    for (let i = 0; i < remaining.length; i++) {
      let score = 0;
      const letters = remaining[i].cleanKab.replace(/\s+/g, '').split('');
      for (const ch of letters) {
        if (placedChars.has(ch)) score += 3;
      }
      if (remaining[i].isPhrase || remaining[i].cleanKab.length <= MAX_COLS) score += 1;

      if (score > bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    }

    if (bestIdx !== -1) {
      orderedWords.push(remaining.splice(bestIdx, 1)[0]);
    } else {
      orderedWords.push(remaining.shift()!);
    }
  }

  // 3. Virtual grid canvas using coordinate map
  const gridMap = new Map<string, GridCellPlacement>();

  const getCell = (r: number, c: number): GridCellPlacement | null => {
    return gridMap.get(`${r},${c}`) || null;
  };

  const setCell = (
    r: number,
    c: number,
    char: string,
    wordId: string,
    dir: 'across' | 'down',
    isSpace?: boolean
  ) => {
    const key = `${r},${c}`;
    let cell = gridMap.get(key);
    if (!cell) {
      cell = { char, acrossWordId: null, downWordId: null, isSpace };
      gridMap.set(key, cell);
    }
    if (dir === 'across') cell.acrossWordId = wordId;
    else cell.downWordId = wordId;
    if (isSpace) cell.isSpace = true;
  };

  const canPlace = (
    wordStr: string,
    r: number,
    c: number,
    dir: 'across' | 'down'
  ): { valid: boolean; intersections: number } | false => {
    const len = wordStr.length;
    const endR = dir === 'across' ? r : r + len - 1;
    const endC = dir === 'across' ? c + len - 1 : c;

    // Strict boundary checks
    if (c < 0 || endC >= MAX_COLS) return false;
    if (r < 0) return false;

    // Check cell immediately before word start (must be empty)
    const prevR = dir === 'across' ? r : r - 1;
    const prevC = dir === 'across' ? c - 1 : c;
    if (getCell(prevR, prevC)) return false;

    // Check cell immediately after word end (must be empty)
    const nextR = dir === 'across' ? r : r + len;
    const nextC = dir === 'across' ? c + len : c;
    if (getCell(nextR, nextC)) return false;

    let intersections = 0;

    for (let i = 0; i < len; i++) {
      const curR = dir === 'across' ? r : r + i;
      const curC = dir === 'across' ? c + i : c;
      const existing = getCell(curR, curC);
      const expectedChar = wordStr[i];

      if (existing) {
        if (existing.isSpace || existing.char !== expectedChar) return false;
        if (dir === 'across' && existing.acrossWordId) return false;
        if (dir === 'down' && existing.downWordId) return false;
        intersections++;
      } else {
        if (dir === 'across') {
          if (getCell(curR - 1, curC) || getCell(curR + 1, curC)) return false;
        } else {
          if (getCell(curR, curC - 1) || getCell(curR, curC + 1)) return false;
        }
      }
    }

    return intersections > 0 ? { valid: true, intersections } : false;
  };

  interface IntermediatePlacedWord {
    norm: NormalizedWord;
    direction: 'across' | 'down';
    row: number;
    col: number;
    step: number;
    attachedToWordId?: string;
    attachType: 'root' | 'intersect' | 'below';
    isPhrase?: boolean;
    cells?: CrosswordWordCell[];
  }

  const placedWords: IntermediatePlacedWord[] = [];

  // Helper to find maximum row occupied so far
  const getCurrentMaxRow = (): number => {
    let maxR = 0;
    placedWords.forEach((pw) => {
      if (pw.cells && pw.cells.length > 0) {
        pw.cells.forEach((c) => {
          if (c.row > maxR) maxR = c.row;
        });
      } else {
        const endR = pw.direction === 'across' ? pw.row : pw.row + pw.norm.cleanKab.length - 1;
        if (endR > maxR) maxR = endR;
      }
    });
    return maxR;
  };

  // 4. Place Word 0 as root
  const firstWord = orderedWords[0];
  if (firstWord.isPhrase && firstWord.phraseWords && firstWord.phraseWords.length > 1) {
    // Lay out phrase horizontally with space cells
    const layout = layoutPhraseHorizontal(firstWord.phraseWords, 0);
    layout.cells.forEach((c) => {
      setCell(c.row, c.col, c.char, firstWord.id, 'across', c.isSpace);
    });
    placedWords.push({
      norm: firstWord,
      direction: 'across',
      row: 0,
      col: 0,
      step: 0,
      attachType: 'root',
      isPhrase: true,
      cells: layout.cells,
    });
  } else {
    // Single word root: place horizontally ('across')
    for (let i = 0; i < firstWord.cleanKab.length; i++) {
      setCell(0, i, firstWord.cleanKab[i], firstWord.id, 'across');
    }

    placedWords.push({
      norm: firstWord,
      direction: 'across',
      row: 0,
      col: 0,
      step: 0,
      attachType: 'root',
    });
  }

  // 5. Place each subsequent word sequentially
  for (let step = 1; step < orderedWords.length; step++) {
    const item = orderedWords[step];

    // PHRASE PLACEMENT: Place horizontally with space cells
    if (item.isPhrase && item.phraseWords && item.phraseWords.length > 1) {
      const maxR = getCurrentMaxRow();
      const attachR = maxR + 2; // Clean 1 empty row separator
      const layout = layoutPhraseHorizontal(item.phraseWords, attachR);

      layout.cells.forEach((c) => {
        setCell(c.row, c.col, c.char, item.id, 'across', c.isSpace);
      });

      placedWords.push({
        norm: item,
        direction: 'across',
        row: attachR,
        col: 0,
        step,
        attachedToWordId: placedWords[placedWords.length - 1].norm.id,
        attachType: 'below',
        isPhrase: true,
        cells: layout.cells,
      });
      continue;
    }

    // SINGLE WORD PLACEMENT:
    const wordStr = item.cleanKab;
    let bestCandidate: {
      startR: number;
      startC: number;
      dir: 'across' | 'down';
      score: number;
      attachedToWordId: string;
    } | null = null;

    // Search from most recently placed back to earliest placed
    for (let pIdx = placedWords.length - 1; pIdx >= 0; pIdx--) {
      const pw = placedWords[pIdx];

      // If parent is a phrase, check intersection with its cells
      if (pw.cells && pw.cells.length > 0) {
        for (let i = 0; i < wordStr.length; i++) {
          const char = wordStr[i];
          for (const pCell of pw.cells) {
            if (!pCell.isSpace && pCell.char === char) {
              const startR = pCell.row - i;
              const startC = pCell.col;
              const res = canPlace(wordStr, startR, startC, 'down');
              if (res && res.valid && startR >= 0) {
                const score = startR * 30 + res.intersections * 20 - Math.abs(startR - pw.row);
                if (!bestCandidate || score > bestCandidate.score) {
                  bestCandidate = {
                    startR,
                    startC,
                    dir: 'down',
                    score,
                    attachedToWordId: pw.norm.id,
                  };
                }
              }
            }
          }
        }
        continue;
      }

      const oppDir = pw.direction === 'across' ? 'down' : 'across';

      // Cannot place across if word length exceeds MAX_COLS
      if (oppDir === 'across' && wordStr.length > MAX_COLS) continue;

      for (let i = 0; i < wordStr.length; i++) {
        const char = wordStr[i];
        for (let j = 0; j < pw.norm.cleanKab.length; j++) {
          if (pw.norm.cleanKab[j] === char) {
            const intersectR = pw.direction === 'across' ? pw.row : pw.row + j;
            const intersectC = pw.direction === 'across' ? pw.col + j : pw.col;

            const startR = oppDir === 'across' ? intersectR : intersectR - i;
            const startC = oppDir === 'across' ? intersectC - i : intersectC;

            const res = canPlace(wordStr, startR, startC, oppDir);
            if (res && res.valid) {
              if (startR < 0) continue;
              const score = startR * 30 + res.intersections * 20 - Math.abs(startR - pw.row);
              if (!bestCandidate || score > bestCandidate.score) {
                bestCandidate = {
                  startR,
                  startC,
                  dir: oppDir,
                  score,
                  attachedToWordId: pw.norm.id,
                };
              }
            }
          }
        }
      }
    }

    if (bestCandidate) {
      for (let i = 0; i < wordStr.length; i++) {
        const r = bestCandidate.dir === 'across' ? bestCandidate.startR : bestCandidate.startR + i;
        const c = bestCandidate.dir === 'across' ? bestCandidate.startC + i : bestCandidate.startC;
        setCell(r, c, wordStr[i], item.id, bestCandidate.dir);
      }
      placedWords.push({
        norm: item,
        direction: bestCandidate.dir,
        row: bestCandidate.startR,
        col: bestCandidate.startC,
        step,
        attachedToWordId: bestCandidate.attachedToWordId,
        attachType: 'intersect',
      });
    } else {
      // Attach underneath lowest row horizontally
      const maxR = getCurrentMaxRow();
      const attachR = maxR + 2;
      const attachDir = 'across';
      const attachCol = 0;

      for (let i = 0; i < wordStr.length; i++) {
        setCell(attachR, attachCol + i, wordStr[i], item.id, attachDir);
      }

      placedWords.push({
        norm: item,
        direction: attachDir,
        row: attachR,
        col: attachCol,
        step,
        attachedToWordId: placedWords[placedWords.length - 1].norm.id,
        attachType: 'below',
      });
    }
  }

  // 6. Calculate total rows and normalize row/col offsets
  let minRow = Infinity;
  let maxRow = -Infinity;
  let minCol = Infinity;
  let maxCol = -Infinity;

  placedWords.forEach((pw) => {
    if (pw.cells && pw.cells.length > 0) {
      pw.cells.forEach((c) => {
        if (c.row < minRow) minRow = c.row;
        if (c.row > maxRow) maxRow = c.row;
        if (c.col < minCol) minCol = c.col;
        if (c.col > maxCol) maxCol = c.col;
      });
    } else {
      const len = pw.norm.cleanKab.length;
      const endR = pw.direction === 'across' ? pw.row : pw.row + len - 1;
      const endC = pw.direction === 'across' ? pw.col + len - 1 : pw.col;

      if (pw.row < minRow) minRow = pw.row;
      if (endR > maxRow) maxRow = endR;
      if (pw.col < minCol) minCol = pw.col;
      if (endC > maxCol) maxCol = endC;
    }
  });

  const rowOffset = minRow < 0 ? -minRow : 0;
  const colOffset = minCol < 0 ? -minCol : 0;
  const totalRows = maxRow + rowOffset + 1;
  const finalCols = Math.max(MAX_COLS, maxCol + colOffset + 1);

  // Keep step order as canonical progression order
  const finalWords: CrosswordWord[] = placedWords.map((pw) => ({
    id: pw.norm.id,
    number: pw.step + 1,
    direction: pw.direction,
    row: pw.row + rowOffset,
    col: pw.col + colOffset,
    kab: pw.norm.cleanKab,
    originalKab: pw.norm.originalKab,
    fr: pw.norm.fr,
    category: pw.norm.category,
    icon: pw.norm.icon,
    culturalNote: pw.norm.culturalNote || `Vocabulaire kabyle : ${pw.norm.originalKab}`,
    phonetic: pw.norm.phonetic,
    step: pw.step,
    attachedToWordId: pw.attachedToWordId,
    attachType: pw.attachType,
    isPhrase: pw.isPhrase,
    cells: pw.cells
      ? pw.cells.map((c) => ({
          ...c,
          row: c.row + rowOffset,
          col: c.col + colOffset,
        }))
      : pw.norm.cleanKab.toUpperCase().split('').map((char, idx) => ({
          row: pw.direction === 'across' ? pw.row + rowOffset : pw.row + rowOffset + idx,
          col: pw.direction === 'across' ? pw.col + colOffset + idx : pw.col + colOffset,
          char,
          isSpace: char === ' ',
        })),
  }));

  return {
    id: `grid_challenge_${challengeId}`,
    title,
    subtitle: `Défi • ${category}`,
    difficulty: finalWords.length <= 15 ? 'Intermédiaire' : 'Maître',
    icon: options?.icon || '🧩',
    rows: totalRows,
    cols: finalCols,
    requiredLevel: options?.requiredLevel ?? 0,
    region: options?.region || 'Kabylie',
    description: `Grille évolutive contenant les ${finalWords.length} mots du défi « ${title} ».`,
    words: finalWords,
    xpReward: 500,
    isEvolving: true,
  };
}

/**
 * In-memory cache of generated crossword grids to prevent expensive recalculations
 * on every view re-render or challenge revisit.
 */
const gridCache = new Map<string, CrosswordGrid[]>();

export function clearCrosswordGridCache(): void {
  gridCache.clear();
}

if (typeof window !== 'undefined') {
  window.addEventListener('kabyle_words_updated', clearCrosswordGridCache);
  window.addEventListener('kabyle_custom_words_updated', clearCrosswordGridCache);
}

/**
 * Splits words into chunks of max 25 words and generates multiple grids per challenge
 * to guarantee all words are covered without overloading a single grid.
 * Results are cached in memory for instant subsequent loads.
 */
export function generateCrosswordGridsForChallenge(
  challengeId: string,
  title: string,
  category: string,
  words: WordItem[],
  options?: {
    icon?: string;
    region?: string;
    requiredLevel?: number;
    maxCols?: number;
  }
): CrosswordGrid[] {
  const cacheKey = `${challengeId}_${words.length}_${words[0]?.kab || ''}_${words[words.length - 1]?.kab || ''}_${options?.maxCols ?? 8}`;
  const cached = gridCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const MAX_WORDS = MAX_WORDS_PER_GRID;
  let result: CrosswordGrid[];

  if (words.length <= MAX_WORDS) {
    result = [generateCrosswordForChallenge(challengeId, title, category, words, options)];
  } else {
    const grids: CrosswordGrid[] = [];
    const totalParts = Math.ceil(words.length / MAX_WORDS);

    for (let i = 0; i < words.length; i += MAX_WORDS) {
      const partNum = Math.floor(i / MAX_WORDS) + 1;
      const chunk = words.slice(i, i + MAX_WORDS);
      const partTitle = `${title} (Grille ${partNum}/${totalParts})`;
      const grid = generateCrosswordForChallenge(
        `${challengeId}_part${partNum}`,
        partTitle,
        category,
        chunk,
        options
      );
      grids.push(grid);
    }
    result = grids;
  }

  // Retain up to 50 cached challenges in memory
  if (gridCache.size > 50) {
    const firstKey = gridCache.keys().next().value;
    if (firstKey) gridCache.delete(firstKey);
  }
  gridCache.set(cacheKey, result);

  return result;
}
